// Shared by the PDP nutrition blocks and the lazily loaded comparison dialog.
window.xmilesNutritionPricing = (() => {
  const massInGrams = {
    kg: 1000, kilogram: 1000, kilograms: 1000,
    g: 1, gram: 1, grams: 1,
    mg: 0.001, milligram: 0.001, milligrams: 0.001,
    ug: 0.000001, mcg: 0.000001, microgram: 0.000001, micrograms: 0.000001,
  };
  const normalizeUnit = (unit) => String(unit || "").trim().toLowerCase()
    .replace(/[µμ]/g, "u").replace(/^(kcal|calories)$/, "calorie");

  const displayUnit = (rawValue, fallbackUnit) => {
    const suffix = String(rawValue ?? "").trim().match(/([a-zµμ]+)\s*$/i)?.[1];
    const unit = normalizeUnit(suffix || fallbackUnit);
    if (Object.hasOwn(massInGrams, unit)) {
      return { 1000: "kg", 1: "gram", 0.001: "mg", 0.000001: "µg" }[massInGrams[unit]];
    }
    return unit;
  };

  const amountInUnit = (rawValue, targetUnit) => {
    const text = String(rawValue ?? "").trim().toLowerCase()
      .replace(/\s+/g, "")
      .replace(/,(?=\d{3}(?:\D|$))/g, "")
      .replace(",", ".");
    const match = text.match(/^(\d+(?:\.\d+)?|\.\d+)([a-zµμ]*)$/);
    if (!match) return null;

    const amount = Number(match[1]);
    if (!Number.isFinite(amount) || amount <= 0) return null;

    const target = normalizeUnit(targetUnit);
    const source = normalizeUnit(match[2]) || target;
    if (source === target) return amount;
    if (Object.hasOwn(massInGrams, source) && Object.hasOwn(massInGrams, target)) {
      return amount * massInGrams[source] / massInGrams[target];
    }
    return null;
  };

  const pricePerUnit = (price, servings, rawValue, unit) => {
    const amount = amountInUnit(rawValue, unit);
    const priceCents = Number(price);
    if (amount === null || !Number.isFinite(priceCents) || priceCents < 0) return null;
    const servingCount = Math.max(1, Number(servings) || 1);
    const value = priceCents / servingCount / amount;
    return Number.isFinite(value) ? value : null;
  };

  const formatPrice = (cents, currency = window.Shopify?.currency?.active || "GBP") => {
    if (!Number.isFinite(cents)) return "";
    const subunit = { GBP: "p", USD: "¢", EUR: "c" }[currency];
    if (subunit && cents < 100) {
      return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(cents)}${subunit}`;
    }
    return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(cents / 100);
  };

  return { displayUnit, amountInUnit, pricePerUnit, formatPrice };
})();

document.addEventListener("alpine:init", () => {
  Alpine.data("product", (options) => ({
    product: null,
    selectedOptions: [],
    selectedVariant: null,
    optionStatus: {},
    optionImages: {},
    swatchOptionIndices: [],
    deliveryDate: "",
    xplus: options.xplus,
    hasAcademiaAccess: !!options.hasAcademiaAccess,
    outlet: !!options.outlet,
    priceOverride: null,
    moneyFormat: options.moneyFormat,
    productLabel: String(options.productLabel || "").trim(),
    nutritionPricing: window.xmilesNutritionPricing,
    nutritionInfo: {},
    quantity: 1,
    backInStockOpen: false,
    backInStockVariant: null,
    backInStockEmail: "",
    backInStockStatus: "idle",
    backInStockMessage: "",
    backInStockPreviousFocus: null,
    backInStockBodyOverflow: "",

    init() {
      const productJsonString = this.$refs.productJson.textContent;
      this.product = JSON.parse(productJsonString);

      if (this.$refs.productPriceOverrideJson) {
        this.priceOverride = JSON.parse(
          this.$refs.productPriceOverrideJson.textContent
        );
      }

      const initialVariant =
        this.product.variants.find((v) => v.id === options.selectedVariantId) ||
        this.product.variants[0];

      this.selectedVariant = this.applyAcademiaSaleDiscount(initialVariant);
      this.selectedOptions = [...initialVariant.options];

      this.initializeOptions(options.swatchOptionNames);
      this.updateState();

      this.deliveryDate = this.getDeliveryDate();
      if (this.$refs.nutritionInfo) {
        const nutritionInfoString = this.$refs.nutritionInfo.textContent;
        this.nutritionInfo = JSON.parse(nutritionInfoString);
      }
      this.$watch("selectedVariant", (variant) => {
        if (!variant) return;
        this.syncVariantSelector(variant.id);
        this.updateMainImage();
        this.updateUrlWithVariantId();
      });

      this.initializeResponsiveProductExtras();
      this.initializeNutritionListWidget();
      this.initializeProductReviewLabels();
      this.initializeFeaturedReviews();
      this.initializeKlaviyoReviewLayout();
    },

    sizesMatch(selectedSize, tableSize) {
      const sizeAliases = {
        extrasmall: "xs",
        xsmall: "xs",
        xs: "xs",
        small: "s",
        s: "s",
        medium: "m",
        m: "m",
        large: "l",
        l: "l",
        extralarge: "xl",
        xlarge: "xl",
        xl: "xl",
        extraextralarge: "xxl",
        xxlarge: "xxl",
        "2xl": "xxl",
        xxl: "xxl",
        smallmedium: "sm",
        sm: "sm",
        mediumlarge: "ml",
        ml: "ml",
        largexlarge: "lxl",
        lxl: "lxl",
      };
      const normalize = (value) => {
        const normalizedValue = String(value || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "");

        return sizeAliases[normalizedValue] || normalizedValue;
      };

      return normalize(selectedSize) === normalize(tableSize);
    },

    initializeProductReviewLabels() {
      const reviewWidgets = [
        ...this.$root.querySelectorAll(".xp-review-stars"),
      ];
      if (!reviewWidgets.length) return;

      const findReviewsSection = () =>
        document.getElementById("xmiles-product-reviews") ||
        document.querySelector(".xmiles-product-reviews");

      const scrollToReviews = () => {
        const reviewsSection = findReviewsSection();
        if (!reviewsSection) return false;

        if (reviewsSection.id && window.location.hash !== `#${reviewsSection.id}`) {
          try {
            window.history.pushState(null, "", `#${reviewsSection.id}`);
          } catch (error) {
            // The smooth scroll still works if browser history is unavailable.
          }
        }

        const reduceMotion = window.matchMedia?.(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        reviewsSection.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "start",
        });
        return true;
      };

      reviewWidgets.forEach((widget) => {
        if (widget.dataset.xpReviewJumpInitialized === "true") return;
        widget.dataset.xpReviewJumpInitialized = "true";

        widget.addEventListener(
          "click",
          (event) => {
            if (event.target.closest('[data-xp-zero-review-trigger="true"]')) {
              return;
            }
            if (!scrollToReviews()) return;

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
          },
          true
        );

        widget.addEventListener("keydown", (event) => {
          if (event.target !== widget) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          if (!scrollToReviews()) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        });
      });

      const findWriteReviewButton = () =>
        [...document.querySelectorAll(".xmiles-product-reviews button")].find(
          (button) =>
            button.textContent
              .replace(/\s+/g, " ")
              .trim()
              .toLowerCase()
              .includes("write a review")
        );

      const openWriteReviewForm = (trigger) => {
        const clickWriteReviewButton = () => {
          const writeReviewButton = findWriteReviewButton();
          if (!writeReviewButton) return false;

          trigger.classList.remove("is-opening-review-form");
          writeReviewButton.click();
          return true;
        };

        if (clickWriteReviewButton()) return;
        if (trigger.classList.contains("is-opening-review-form")) return;

        trigger.classList.add("is-opening-review-form");
        scrollToReviews();

        const writeReviewObserver = new MutationObserver(() => {
          if (!clickWriteReviewButton()) return;
          writeReviewObserver.disconnect();
          window.clearTimeout(writeReviewTimeout);
        });
        writeReviewObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });

        const writeReviewTimeout = window.setTimeout(() => {
          writeReviewObserver.disconnect();
          trigger.classList.remove("is-opening-review-form");
        }, 20000);
      };

      const updateZeroReviewLabels = () => {
        reviewWidgets.forEach((widget) => {
          const label = widget.querySelector(
            ".kl_reviews__star_rating_widget__label"
          );
          if (!label) return;

          const widgetTrigger = label.closest("button, a");
          if (widgetTrigger) {
            widget.removeAttribute("role");
            widget.removeAttribute("tabindex");
            widgetTrigger.setAttribute("aria-label", "Read product reviews");
          } else {
            widget.setAttribute("role", "link");
            widget.setAttribute("tabindex", "0");
            widget.setAttribute("aria-label", "Read product reviews");
          }

          const countMatch = label.textContent
            .replace(/,/g, "")
            .match(/(\d+)\s+reviews?/i);
          if (!countMatch || Number(countMatch[1]) !== 0) return;

          label.textContent = "Be the first to review";
          widget.classList.add("has-zero-reviews");

          const trigger = label.closest("button, a") || label;
          if (trigger.dataset.xpZeroReviewTrigger === "true") return;

          trigger.dataset.xpZeroReviewTrigger = "true";
          trigger.setAttribute("aria-label", "Be the first to review this product");
          if (trigger === label) {
            trigger.setAttribute("role", "button");
            trigger.setAttribute("tabindex", "0");
            trigger.addEventListener("keydown", (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              openWriteReviewForm(trigger);
            });
          }

          trigger.addEventListener(
            "click",
            (event) => {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              openWriteReviewForm(trigger);
            },
            true
          );
        });
      };

      const observer = new MutationObserver(updateZeroReviewLabels);
      reviewWidgets.forEach((widget) => {
        observer.observe(widget, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      });
      updateZeroReviewLabels();
      window.setTimeout(() => observer.disconnect(), 20000);
    },

    initializeFeaturedReviews() {
      const featuredReviews = this.$root.querySelector(
        "[data-xp-featured-reviews]"
      );
      const reviewGrid = featuredReviews?.querySelector(
        "[data-xp-featured-reviews-grid]"
      );
      if (!featuredReviews || !reviewGrid) return;
      // Alpine can revisit this component while other PDP widgets update.
      // Keep one observer and preserve the three reviews already shown.
      if (featuredReviews.dataset.xpFeaturedReviewsInitialized === "true") return;
      featuredReviews.dataset.xpFeaturedReviewsInitialized = "true";

      const cleanText = (value) => (value || "").replace(/\s+/g, " ").trim();

      const createTextElement = (tagName, className, text) => {
        const element = document.createElement(tagName);
        element.className = className;
        element.textContent = text;
        return element;
      };

      const populateFeaturedReviews = () => {
        if (reviewGrid.querySelectorAll(".xp-featured-review").length === 3) {
          featuredReviews.hidden = false;
          return true;
        }
        const reviewItems = [
          ...document.querySelectorAll(
            ".xmiles-product-reviews .kl_reviews__review_item"
          ),
        ];
        const fiveStarReviews = reviewItems
          .filter((item) =>
            item.querySelector('[aria-label*="5 star review" i]')
          )
          .map((item) => ({
            title: cleanText(
              item.querySelector(".kl_reviews__review__title")?.textContent
            ),
            content: cleanText(
              item.querySelector(".kl_reviews__review__content")?.textContent
            ),
            author: cleanText(
              item.querySelector(".kl_reviews__review__author > div:first-child")
                ?.textContent
            ),
            date: cleanText(
              item.querySelector(".kl_reviews__review__timestamp")?.textContent
            ),
            verified: Boolean(
              item.querySelector(".kl_reviews__review__verified")
            ),
          }))
          .filter((review) => review.content);

        if (fiveStarReviews.length < 3) {
          // Klaviyo may briefly empty its list during loading or filtering.
          // An incomplete source must not erase an existing featured row.
          featuredReviews.hidden = reviewGrid.childElementCount === 0;
          return false;
        }

        for (let index = fiveStarReviews.length - 1; index > 0; index -= 1) {
          const randomIndex = Math.floor(Math.random() * (index + 1));
          [fiveStarReviews[index], fiveStarReviews[randomIndex]] = [
            fiveStarReviews[randomIndex],
            fiveStarReviews[index],
          ];
        }

        const cards = fiveStarReviews.slice(0, 3).map((review) => {
          const card = document.createElement("a");
          card.className = "xp-featured-review";
          card.href = "#xmiles-product-reviews";
          card.setAttribute("aria-label", "Read all product reviews");

          const stars = createTextElement(
            "div",
            "xp-featured-review__stars",
            "★★★★★"
          );
          stars.setAttribute("role", "img");
          stars.setAttribute("aria-label", "5 out of 5 stars");
          card.appendChild(stars);

          if (review.title) {
            card.appendChild(
              createTextElement("h3", "xp-featured-review__title", review.title)
            );
          }

          card.appendChild(
            createTextElement("p", "xp-featured-review__content", review.content)
          );

          const footer = document.createElement("footer");
          footer.className = "xp-featured-review__footer";
          const customer = document.createElement("div");
          customer.className = "xp-featured-review__customer";
          customer.appendChild(
            createTextElement(
              "strong",
              "xp-featured-review__author",
              review.author || "XMiles customer"
            )
          );
          if (review.verified) {
            customer.appendChild(
              createTextElement(
                "span",
                "xp-featured-review__verified",
                "✓ Verified buyer"
              )
            );
          }
          footer.appendChild(customer);
          if (review.date) {
            footer.appendChild(
              createTextElement("span", "xp-featured-review__date", review.date)
            );
          }
          card.appendChild(footer);
          return card;
        });

        reviewGrid.replaceChildren(...cards);
        featuredReviews.style.setProperty(
          "--xp-featured-review-count",
          String(cards.length)
        );
        featuredReviews.hidden = false;
        return true;
      };

      if (populateFeaturedReviews()) return;

      let reviewObserver = null;
      const observeReviewSection = () => {
        const reviewsSection = document.querySelector(".xmiles-product-reviews");
        if (!reviewsSection) return false;

        const populateAndStop = () => {
          if (!populateFeaturedReviews()) return;
          reviewObserver?.disconnect();
        };

        reviewObserver = new MutationObserver(populateAndStop);
        reviewObserver.observe(reviewsSection, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ["aria-label"],
        });
        populateAndStop();
        return true;
      };

      if (observeReviewSection()) return;

      const pageObserver = new MutationObserver(() => {
        if (!observeReviewSection()) return;
        pageObserver.disconnect();
      });
      pageObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    },

    initializeKlaviyoReviewLayout() {
      let reviewsSection = null;
      let reviewObserver = null;
      let sectionObserver = null;
      let decorateTimer = null;
      const cleanText = (value) => (value || "").replace(/\s+/g, " ").trim();

      const decorateReview = (review) => {
        if (review.dataset.xpReviewLayout === "true") return;

        const starsAndTime = review.querySelector(
          ".kl_reviews__review_stars_and_time"
        );
        const author = review.querySelector(".kl_reviews__review__author");
        const title = review.querySelector(".kl_reviews__review__title");
        const content = review.querySelector(".kl_reviews__review__content");
        if (!starsAndTime || !content) return;
        review.dataset.xpReviewLayout = "true";

        const questions = [
          ...review.querySelectorAll(".kl_reviews__custom_question_answer"),
        ];
        const demographicQuestions = questions.filter((question) =>
          question.querySelector(".kl_reviews__custom_question__question--text")
        );
        const sliderQuestions = questions.filter((question) =>
          question.querySelector(".kl_reviews__custom_question__question--range")
        );

        const layout = document.createElement("div");
        layout.className = "xp-klaviyo-review__layout";

        const identity = document.createElement("div");
        identity.className = "xp-klaviyo-review__identity";
        if (author) identity.appendChild(author);

        if (demographicQuestions.length) {
          const demographics = document.createElement("div");
          demographics.className = "xp-klaviyo-review__demographics";
          demographicQuestions.forEach((question) => {
            const label = question.querySelector(
              ".kl_reviews__custom_question__question--text"
            );
            const labelText = cleanText(label?.textContent).toLowerCase();
            if (label) {
              if (labelText.includes("sport")) label.textContent = "Sport";
              if (labelText.includes("age")) label.textContent = "Age";
              if (labelText.includes("gender")) label.textContent = "Gender";
            }
            demographics.appendChild(question);
          });
          identity.appendChild(demographics);
        }
        layout.appendChild(identity);

        if (sliderQuestions.length) {
          const sliders = document.createElement("div");
          sliders.className = "xp-klaviyo-review__sliders";
          sliderQuestions.forEach((question) => sliders.appendChild(question));
          layout.appendChild(sliders);
        } else {
          layout.classList.add("xp-klaviyo-review__layout--without-sliders");
        }

        const reviewText = document.createElement("div");
        reviewText.className = "xp-klaviyo-review__text";
        if (title && cleanText(title.textContent)) reviewText.appendChild(title);
        reviewText.appendChild(content);
        layout.appendChild(reviewText);

        const originalContent = starsAndTime.nextElementSibling;
        if (originalContent && !cleanText(originalContent.textContent)) {
          originalContent.remove();
        }

        review
          .querySelectorAll(".kl_reviews__review__attributes")
          .forEach((attributes) => attributes.remove());

        starsAndTime.parentElement.insertAdjacentElement("afterend", layout);
      };

      const decorateReviews = () => {
        decorateTimer = null;
        if (!reviewsSection?.isConnected) return;

        const reviewWrapper = reviewsSection.querySelector(
          "#klaviyo-product-reviews-wrapper"
        );
        reviewsSection.classList.toggle("is-ready", Boolean(reviewWrapper));
        reviewsSection.classList.toggle("is-loading", !reviewWrapper);

        if (!reviewWrapper) return;
        reviewsSection
          .querySelectorAll(
            ".kl_reviews__review_item:not([data-xp-review-layout='true'])"
          )
          .forEach(decorateReview);
      };

      const scheduleDecorate = () => {
        window.clearTimeout(decorateTimer);
        decorateTimer = window.setTimeout(decorateReviews, 100);
      };

      const connectReviewSection = () => {
        const nextReviewsSection = document.querySelector(
          ".xmiles-product-reviews"
        );
        if (!nextReviewsSection) return false;
        if (reviewsSection === nextReviewsSection && reviewObserver) {
          scheduleDecorate();
          return true;
        }

        reviewObserver?.disconnect();
        reviewsSection = nextReviewsSection;
        reviewsSection.classList.add("is-loading");
        reviewsSection.classList.remove("is-ready");

        reviewObserver = new MutationObserver(scheduleDecorate);
        reviewObserver.observe(reviewsSection, {
          childList: true,
          subtree: true,
        });
        scheduleDecorate();
        return true;
      };

      if (!connectReviewSection()) {
        sectionObserver = new MutationObserver(() => {
          if (!connectReviewSection()) return;
          sectionObserver.disconnect();
        });
        sectionObserver.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      }

      window.addEventListener(
        "load",
        () => {
          connectReviewSection();
          scheduleDecorate();
        },
        { once: true }
      );

      document.addEventListener("shopify:section:load", () => {
        connectReviewSection();
      });
    },

    initializeNutritionListWidget() {
      const root = this.$root;
      const widget = root.querySelector("[data-xp-nutrition-list-app-host]");
      const slot = root.querySelector("[data-xp-nutrition-list-action-slot]");
      const actionButton = root.querySelector("[data-xp-nutrition-list-toggle]");
      const popover = root.querySelector("[data-xp-nutrition-list-popover]");
      const closeButton = root.querySelector("[data-xp-nutrition-list-close]");
      const successPanel = root.querySelector("[data-xp-nutrition-list-success]");
      const successName = root.querySelector("[data-xp-nutrition-list-success-name]");
      const viewListLink = root.querySelector("[data-xp-nutrition-list-view]");
      const chooseAnotherButton = root.querySelector("[data-xp-nutrition-list-again]");
      const card = root.querySelector(".xp-cta__card");
      const quantityInput = root.querySelector(".xp-quantity-input");
      if (
        !widget ||
        !slot ||
        !actionButton ||
        !popover ||
        !closeButton ||
        !successPanel ||
        !successName ||
        !viewListLink ||
        !chooseAnotherButton ||
        !card ||
        !quantityInput
      ) return;

      const status = widget.querySelector("[data-xp-nutrition-list-status]");
      let statusTimer = null;
      let animationFrame = null;
      let pickerRequestPending = false;
      let pickerRequestVersion = 0;
      let pickerRequestAttempts = 0;
      let quantityUpdatePending = false;
      let quantityUpdateVersion = 0;
      let savedSuccess = null;
      let suppressNativeSuccess = false;
      let pageObserver = null;
      let activeVariantId = null;

      const cleanText = (value) => (value || "").replace(/\s+/g, " ").trim();

      const selectedQuantity = () =>
        Math.max(1, Number.parseInt(quantityInput.value, 10) || 1);

      const syncListQuantity = () => {
        const quantity = selectedQuantity();
        widget.dataset.quantity = String(quantity);

        const appRoot = widget.querySelector("#xmt-nutrition-pdp");
        if (appRoot) {
          appRoot.dataset.quantity = String(quantity);
          appRoot.dataset.productQuantity = String(quantity);
        }

        const appForm = widget.querySelector("form");
        if (appForm && !appForm.querySelector("[data-xp-nutrition-list-quantity]")) {
          const quantityProxy = document.createElement("input");
          quantityProxy.type = "hidden";
          quantityProxy.name = "quantity";
          quantityProxy.dataset.xpNutritionListQuantity = "true";
          appForm.appendChild(quantityProxy);
        }

        widget.querySelectorAll("input[name='quantity']").forEach((input) => {
          if (input === quantityInput || input.value === String(quantity)) return;
          input.value = String(quantity);
          if (input.hasAttribute("data-xp-nutrition-list-quantity")) return;
          input.dispatchEvent(new Event("input", { bubbles: true }));
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      };

      const getNativeTrigger = () =>
        widget.querySelector(".xmt-show-list-button") ||
        [...widget.querySelectorAll("button, a, [role='button']")].find((control) => {
          const label = cleanText(
            control.getAttribute("aria-label") || control.textContent
          ).toLowerCase();
          return (
            label.includes("add to nutrition list") ||
            label.includes("add to list") ||
            label.includes("save to nutrition list")
          );
        });

      const resetNativePickerState = () => {
        const appRoot = widget.querySelector("#xmt-nutrition-pdp");
        const appState = appRoot && window.Alpine?.$data
          ? window.Alpine.$data(appRoot)
          : null;
        if (!appState) return false;

        if ("response" in appState) appState.response = {};
        if ("selectedList" in appState) appState.selectedList = null;
        if ("showForm" in appState) appState.showForm = false;
        return true;
      };

      const nativeSuccessIsPresent = () => {
        const widgetText = cleanText(widget.textContent).toLowerCase();
        return (
          widgetText.includes("item added to list successfully") ||
          widgetText.includes("added to list successfully")
        );
      };

      const pickerIsVisible = () => {
        return [...widget.querySelectorAll("select, [role='listbox'], [role='option'], input:not([type='hidden']), a[href*='/apps/xmiles-nutrition/lists/new']")].some(
          (element) => {
            const style = window.getComputedStyle(element);
            const bounds = element.getBoundingClientRect();
            return (
              !element.hidden &&
              style.display !== "none" &&
              style.visibility !== "hidden" &&
              element.getClientRects().length > 0 &&
              bounds.width > 10 &&
              bounds.height > 10
            );
          }
        );
      };

      const pickerIsReady = () =>
        pickerIsVisible() && !nativeSuccessIsPresent();

      const selectedListName = () => {
        const select = widget.querySelector("select");
        const option = select?.selectedOptions?.[0];
        return cleanText(option?.textContent || select?.value);
      };

      const selectedListId = () =>
        widget.querySelector("select[name='listId']")?.value || null;

      const selectedVariantId = () =>
        root.querySelector("[data-xp-variant-id]")?.value ||
        root.querySelector("input[name='id']")?.value ||
        null;

      const selectedProductId = () =>
        widget.querySelector("input[name='productId']")?.value || null;

      const parseListItems = (html) => {
        const parsedDocument = new DOMParser().parseFromString(html, "text/html");
        const listDataElement = [...parsedDocument.querySelectorAll("[x-data]")].find(
          (element) => {
            const data = element.getAttribute("x-data") || "";
            return data.trim().startsWith("list({") && data.includes("items:");
          }
        );
        const listData = listDataElement?.getAttribute("x-data") || "";
        const itemsMarker = listData.indexOf("items:");
        const arrayStart = listData.indexOf("[", itemsMarker);
        if (itemsMarker < 0 || arrayStart < 0) {
          throw new Error("Nutrition list items were not found");
        }

        let depth = 0;
        let quote = null;
        let escaped = false;
        for (let index = arrayStart; index < listData.length; index += 1) {
          const character = listData[index];

          if (quote) {
            if (escaped) {
              escaped = false;
            } else if (character === "\\") {
              escaped = true;
            } else if (character === quote) {
              quote = null;
            }
            continue;
          }

          if (character === '"' || character === "'") {
            quote = character;
          } else if (character === "[") {
            depth += 1;
          } else if (character === "]") {
            depth -= 1;
            if (depth === 0) {
              return JSON.parse(listData.slice(arrayStart, index + 1));
            }
          }
        }

        throw new Error("Nutrition list items could not be read");
      };

      const loadListItems = async (listId) => {
        const response = await fetch(
          `/apps/xmiles-nutrition/lists/${encodeURIComponent(listId)}/?xp_quantity_sync=${Date.now()}`,
          { credentials: "same-origin", cache: "no-store" }
        );
        if (!response.ok) throw new Error("Nutrition list could not be loaded");
        return parseListItems(await response.text());
      };

      const findAddedListItem = async ({ listId, variantId, productId }) => {
        for (let attempt = 0; attempt < 4; attempt += 1) {
          const items = await loadListItems(listId);
          const variantMatches = variantId
            ? items.filter(
                (item) => String(item.variantId) === String(variantId)
              )
            : [];
          const productMatches = productId
            ? items.filter(
                (item) => String(item.productId) === String(productId)
              )
            : [];
          const matchingItems = variantMatches.length
            ? variantMatches
            : productMatches;
          const item = matchingItems.sort(
            (first, second) => Number(second.id) - Number(first.id)
          )[0];
          if (item) return item;

          await new Promise((resolve) =>
            window.setTimeout(resolve, 250 * (attempt + 1))
          );
        }

        throw new Error("The added nutrition list item could not be found");
      };

      const updateAddedItemQuantity = async ({
        listId,
        variantId,
        productId,
        quantity,
      }) => {
        const item = await findAddedListItem({ listId, variantId, productId });
        const response = await fetch("/apps/xmiles-nutrition/api/lists/item/update", {
          method: "POST",
          credentials: "same-origin",
          body: JSON.stringify({
            listId: Number(listId) || listId,
            itemId: item.id,
            quantity,
          }),
        });
        if (!response.ok) {
          throw new Error("Nutrition list quantity could not be updated");
        }
      };

      const showSavedSuccess = () => {
        if (!savedSuccess) return;

        const quantityLabel = `${savedSuccess.quantity} ${
          savedSuccess.quantity === 1 ? "unit" : "units"
        }`;
        successName.textContent = savedSuccess.listName
          ? `${quantityLabel} · ${savedSuccess.listName}`
          : quantityLabel;
        viewListLink.href = savedSuccess.viewUrl || "/apps/xmiles-nutrition/lists";
        widget.hidden = true;
        successPanel.hidden = false;
        popover.classList.remove("is-loading", "is-updating-quantity");
        pageObserver?.disconnect();
        pageObserver = null;
      };

      const resetSavedSuccess = () => {
        savedSuccess = null;
        successPanel.hidden = true;
        widget.hidden = false;
        popover.classList.remove("is-updating-quantity");
        window.clearTimeout(statusTimer);
        if (status) {
          status.classList.remove("is-visible");
          status.hidden = true;
        }
      };

      const captureSuccess = () => {
        const successFound = nativeSuccessIsPresent();
        if (!successFound) {
          if (pickerIsReady()) suppressNativeSuccess = false;
          return false;
        }
        if (suppressNativeSuccess) return false;
        if (savedSuccess) {
          showSavedSuccess();
          return true;
        }

        const nativeViewLink = [...widget.querySelectorAll("a")].find((link) =>
          cleanText(link.textContent).toLowerCase().includes("view list")
        );
        const listId = selectedListId();
        const listName = selectedListName();
        const quantity = selectedQuantity();
        const variantId = selectedVariantId();
        const productId = selectedProductId();
        const viewUrl =
          nativeViewLink?.getAttribute("href") ||
          (listId
            ? `/apps/xmiles-nutrition/lists/${encodeURIComponent(listId)}/`
            : "/apps/xmiles-nutrition/lists");

        if (quantity > 1 && listId && !quantityUpdatePending) {
          const updateVersion = ++quantityUpdateVersion;
          quantityUpdatePending = true;
          popover.classList.add("is-loading", "is-updating-quantity");

          updateAddedItemQuantity({ listId, variantId, productId, quantity })
            .then(() => {
              if (
                updateVersion !== quantityUpdateVersion ||
                popover.hidden
              ) return;
              savedSuccess = { listName, quantity, viewUrl };
              showSavedSuccess();
            })
            .catch((error) => {
              console.error("Unable to update nutrition list quantity:", error);
              if (updateVersion === quantityUpdateVersion) {
                popover.classList.remove("is-loading", "is-updating-quantity");
              }
            })
            .finally(() => {
              if (updateVersion === quantityUpdateVersion) {
                quantityUpdatePending = false;
              }
              scheduleDecorate();
            });
          return true;
        }

        if (quantityUpdatePending) return true;

        savedSuccess = { listName, quantity, viewUrl };
        showSavedSuccess();
        return true;
      };

      const showStatus = (control) => {
        if (!status) return;

        const controlText = cleanText(control.textContent);
        const itemContainer = control.closest(
          "[data-list-id], [data-list], [role='option'], li"
        );
        const itemCandidates = itemContainer
          ? itemContainer.querySelectorAll(
              "[data-product-title], [data-item-title], .product-title, .list-item__title, img[alt]"
            )
          : [];
        const itemNames = [...itemCandidates]
          .map((item) =>
            cleanText(
              item.getAttribute("data-product-title") ||
                item.getAttribute("data-item-title") ||
                item.getAttribute("alt") ||
                item.textContent
            )
          )
          .filter((name, index, names) => name && names.indexOf(name) === index)
          .slice(0, 3);

        status.textContent = itemNames.length
          ? `${controlText}: ${itemNames.join(" • ")}`
          : `${controlText} selected`;
        if (itemNames.length) {
          status.textContent = `${controlText}: ${itemNames.join(" / ")}`;
        }
        status.hidden = false;
        status.classList.add("is-visible");

        window.clearTimeout(statusTimer);
        statusTimer = window.setTimeout(() => {
          status.classList.remove("is-visible");
          window.setTimeout(() => {
            status.hidden = true;
          }, 180);
        }, 3200);
      };

      const decorateControl = (control) => {
        const label = cleanText(control.textContent).toLowerCase();
        if (!label) return;

        if (
          label.includes("add to list") ||
          label.includes("add to nutrition") ||
          label.includes("save to list")
        ) {
          control.classList.add("xp-nutrition-list-trigger");
        }

        const isListChoice =
          control.matches("[role='option'], [data-list-id], [data-list]") ||
          (label.includes("list") &&
            !label.includes("add to") &&
            !label.includes("create") &&
            !label.includes("new list") &&
            !label.includes("your lists") &&
            !label.includes("manage lists")) ||
          (control.closest(".xp-nutrition-list-dialog") &&
            control.matches("button, [role='button']") &&
            !label.includes("close") &&
            !label.includes("cancel") &&
            !label.includes("add") &&
            !label.includes("create"));

        if (isListChoice) {
          control.classList.add("xp-nutrition-list-choice");
          if (!control.dataset.xpListPreviewBound) {
            control.dataset.xpListPreviewBound = "true";
            control.addEventListener("click", () => {
              document
                .querySelectorAll(".xp-nutrition-list-choice.is-selected")
                .forEach((item) => item.classList.remove("is-selected"));
              control.classList.add("is-selected");
              showStatus(control);
            });
          }
        }
      };

      const decorate = () => {
        animationFrame = null;

        syncListQuantity();

        widget
          .querySelectorAll("button, a, [role='button'], [role='option']")
          .forEach(decorateControl);

        const trigger = getNativeTrigger();

        if (trigger) {
          trigger.classList.add("xp-nutrition-list-native-trigger");
          trigger.setAttribute("aria-hidden", "true");
          trigger.setAttribute("tabindex", "-1");
        }

        captureSuccess();

        if (!popover.hidden) {
          document
            .querySelectorAll(
              "[role='dialog'], dialog, [aria-modal='true'], [class*='modal' i], [class*='popup' i]"
            )
            .forEach((dialog) => {
              if (
                !dialog.matches("[data-xp-nutrition-list-popover]") &&
                cleanText(dialog.textContent).toLowerCase().includes("list")
              ) {
                dialog.classList.add("xp-nutrition-list-dialog");
                dialog
                  .querySelectorAll("button, a, [role='button'], [role='option']")
                  .forEach(decorateControl);
              }
            });
        }

        const pickerVisible = pickerIsReady();
        if (pickerVisible) pickerRequestAttempts = 0;

        if (!popover.hidden && savedSuccess) {
          showSavedSuccess();
        } else if (!popover.hidden && quantityUpdatePending) {
          popover.classList.add("is-loading", "is-updating-quantity");
        } else if (!popover.hidden) {
          popover.classList.remove("is-updating-quantity");
          popover.classList.toggle("is-loading", !pickerVisible);
          if (!pickerVisible) requestNativePicker();
        }
      };

      const scheduleDecorate = () => {
        if (animationFrame !== null) return;
        animationFrame = window.requestAnimationFrame(decorate);
      };

      const observePageWhileOpen = () => {
        if (pageObserver) return;
        pageObserver = new MutationObserver(scheduleDecorate);
        pageObserver.observe(document.body, { childList: true, subtree: true });
      };

      const stopObservingPage = () => {
        pageObserver?.disconnect();
        pageObserver = null;
      };

      const requestNativePicker = () => {
        const nativeTrigger = getNativeTrigger();
        if (
          !nativeTrigger ||
          pickerIsReady() ||
          pickerRequestPending ||
          pickerRequestAttempts >= 3
        ) return;

        const requestVersion = ++pickerRequestVersion;
        pickerRequestAttempts += 1;
        pickerRequestPending = true;
        syncListQuantity();
        nativeTrigger.focus({ preventScroll: true });
        nativeTrigger.click();
        window.setTimeout(() => {
          if (requestVersion !== pickerRequestVersion) return;
          pickerRequestPending = false;
          scheduleDecorate();
        }, 400);
      };

      const positionPopover = () => {
        const cardBounds = card.getBoundingClientRect();
        const buttonBounds = actionButton.getBoundingClientRect();
        popover.style.top = `${buttonBounds.bottom - cardBounds.top + 10}px`;
        popover.style.right = `${Math.max(0, cardBounds.right - buttonBounds.right)}px`;
      };

      const openPopover = () => {
        pickerRequestVersion += 1;
        pickerRequestPending = false;
        pickerRequestAttempts = 0;
        quantityUpdateVersion += 1;
        quantityUpdatePending = false;
        if (nativeSuccessIsPresent()) {
          suppressNativeSuccess = true;
          resetNativePickerState();
        }
        resetSavedSuccess();
        popover.hidden = false;
        popover.classList.add("is-open");
        popover.classList.add("is-loading");
        actionButton.setAttribute("aria-expanded", "true");
        observePageWhileOpen();
        positionPopover();
        decorate();
        requestNativePicker();

        window.requestAnimationFrame(() => {
          const firstControl = popover.querySelector(
            "select, input:not([type='hidden']), button:not([data-xp-nutrition-list-close]):not(.xp-nutrition-list-native-trigger), a"
          );
          if (firstControl) firstControl.focus({ preventScroll: true });
        });
      };

      const closePopover = () => {
        const nativeStateNeedsReset = savedSuccess || nativeSuccessIsPresent();
        pickerRequestVersion += 1;
        pickerRequestPending = false;
        pickerRequestAttempts = 0;
        quantityUpdateVersion += 1;
        quantityUpdatePending = false;
        popover.classList.remove("is-open");
        popover.hidden = true;
        actionButton.setAttribute("aria-expanded", "false");
        if (nativeStateNeedsReset) suppressNativeSuccess = true;
        resetSavedSuccess();
        if (nativeStateNeedsReset) resetNativePickerState();
        stopObservingPage();
      };

      actionButton.addEventListener("click", () => {
        if (popover.hidden) {
          openPopover();
        } else {
          closePopover();
        }
      });

      closeButton.addEventListener("click", () => {
        closePopover();
        actionButton.focus({ preventScroll: true });
      });

      chooseAnotherButton.addEventListener("click", () => {
        pickerRequestVersion += 1;
        pickerRequestPending = false;
        pickerRequestAttempts = 0;
        quantityUpdateVersion += 1;
        quantityUpdatePending = false;
        suppressNativeSuccess = true;
        resetSavedSuccess();
        resetNativePickerState();
        popover.classList.add("is-loading");
        observePageWhileOpen();
        decorate();
        requestNativePicker();

        window.requestAnimationFrame(() => {
          const select = widget.querySelector("select");
          if (select) select.focus({ preventScroll: true });
        });
      });

      widget.addEventListener(
        "click",
        (event) => {
          syncListQuantity();
          const control = event.target.closest(
            "button, input[type='button'], input[type='submit'], [role='button']"
          );
          if (!control || !widget.contains(control)) return;

          const label = cleanText(
            control.getAttribute("aria-label") ||
              control.value ||
              control.textContent
          ).toLowerCase();
          if (label.includes("add to list")) syncListQuantity();
        },
        true
      );

      quantityInput.addEventListener("input", syncListQuantity);
      quantityInput.addEventListener("change", syncListQuantity);

      activeVariantId = selectedVariantId();
      window.addEventListener("variant:updated", () => {
        window.requestAnimationFrame(() => {
          const nextVariantId = selectedVariantId();
          if (!nextVariantId || nextVariantId === activeVariantId) return;

          activeVariantId = nextVariantId;
          suppressNativeSuccess = true;
          closePopover();
          resetNativePickerState();
        });
      });

      document.addEventListener("click", (event) => {
        if (
          !popover.hidden &&
          !slot.contains(event.target) &&
          !popover.contains(event.target)
        ) {
          closePopover();
        }
      });

      window.addEventListener("resize", () => {
        if (!popover.hidden) positionPopover();
      });

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !popover.hidden) {
          closePopover();
          actionButton.focus({ preventScroll: true });
        }
      });

      const observer = new MutationObserver(scheduleDecorate);
      observer.observe(widget, { childList: true, subtree: true });
      scheduleDecorate();
    },

    initializeResponsiveProductExtras() {
      const root = this.$root;
      const destination = root.querySelector("[data-xp-mobile-product-extras]");
      const factsDestination = root.querySelector("[data-xp-mobile-nutrition-facts]");
      const accountDestination = root.querySelector("[data-xp-mobile-account-banner]");
      const accountOrigin = root.querySelector("[data-xp-account-banner-origin]");
      const featuredReviewsOrigin = root.querySelector("[data-xp-featured-reviews-origin]");
      const media = root.querySelector(".xp-media");
      const gallery = root.querySelector(".xp-gallery");
      const info = root.querySelector(".xp-info");
      const ctaCard = root.querySelector(".xp-cta__card");
      const nutritionOrigin = root.querySelector("[data-xp-nutrition-origin]");
      const sizeChartOrigin = root.querySelector("[data-xp-size-chart-origin]");
      const packContentsOrigin = root.querySelector("[data-xp-pack-contents-origin]");

      if (!destination || !factsDestination || !accountDestination || !accountOrigin || !media || !gallery) return;

      const mobileQuery = window.matchMedia("(max-width: 1000px)");
      let animationFrame = null;

      const placeExtras = () => {
        animationFrame = null;

        const productLabels = root.querySelector("[data-xp-product-labels]");
        const nutritionFacts = root.querySelector("[data-xp-nutrition-facts]");
        const nutritionPanel = root.querySelector("[data-xp-nutrition-panel]");
        const sizeChart = root.querySelector(".xp-size-chart");
        const packContents = root.querySelector("[data-xp-pack-contents]");
        const nutritionListAction = root.querySelector(
          "[data-xp-nutrition-list-action-slot]"
        );
        const accountBanner = root.querySelector(".xp-account-banner-row");
        const featuredReviews = root.querySelector("[data-xp-featured-reviews]");
        const promoBanner = root.querySelector(".xp-promo-banner");

        if (productLabels && productLabels.parentElement !== gallery) {
          gallery.appendChild(productLabels);
        }
        if (
          nutritionListAction &&
          nutritionListAction.parentElement !== gallery
        ) {
          gallery.appendChild(nutritionListAction);
        }

        if (mobileQuery.matches) {
          if (promoBanner && ctaCard && ctaCard.firstElementChild !== promoBanner) {
            ctaCard.prepend(promoBanner);
          }
          if (nutritionFacts && nutritionFacts.parentElement !== factsDestination) {
            factsDestination.appendChild(nutritionFacts);
          }
          if (sizeChart) {
            const nutritionPanelAnchor =
              nutritionPanel?.parentElement === destination ? nutritionPanel : null;
            if (
              sizeChart.parentElement !== destination ||
              (nutritionPanelAnchor && sizeChart.nextElementSibling !== nutritionPanelAnchor)
            ) {
              destination.insertBefore(sizeChart, nutritionPanelAnchor);
            }
          }
          if (packContents) {
            const nutritionPanelAnchor =
              nutritionPanel?.parentElement === destination ? nutritionPanel : null;
            if (
              packContents.parentElement !== destination ||
              (nutritionPanelAnchor && packContents.nextElementSibling !== nutritionPanelAnchor)
            ) {
              destination.insertBefore(packContents, nutritionPanelAnchor);
            }
          }
          if (nutritionPanel && nutritionPanel.parentElement !== destination) {
            destination.appendChild(nutritionPanel);
          }
          if (accountBanner) {
            if (featuredReviews && featuredReviews.parentElement !== accountDestination) {
              accountDestination.appendChild(featuredReviews);
            }
            if (accountBanner.parentElement !== accountDestination) {
              accountDestination.appendChild(accountBanner);
            }
          }
          if (nutritionOrigin && nutritionOrigin.parentElement) {
            nutritionOrigin.parentElement.classList.add("is-mobile-nutrition-moved");
          }
          return;
        }

        if (nutritionOrigin && nutritionOrigin.parentElement) {
          nutritionOrigin.parentElement.classList.remove("is-mobile-nutrition-moved");
        }

        if (promoBanner && info && info.firstElementChild !== promoBanner) {
          info.prepend(promoBanner);
        }

        if (
          accountBanner &&
          accountOrigin.parentElement &&
          accountOrigin.nextElementSibling !== accountBanner
        ) {
          accountOrigin.parentElement.insertBefore(accountBanner, accountOrigin.nextSibling);
        }
        if (
          featuredReviews &&
          featuredReviewsOrigin &&
          featuredReviewsOrigin.parentElement &&
          featuredReviewsOrigin.nextElementSibling !== featuredReviews
        ) {
          featuredReviewsOrigin.parentElement.insertBefore(
            featuredReviews,
            featuredReviewsOrigin.nextSibling
          );
        }
        if (nutritionFacts && nutritionFacts.parentElement !== media) {
          media.appendChild(nutritionFacts);
        }
        if (
          sizeChart &&
          sizeChartOrigin?.parentElement &&
          sizeChartOrigin.nextElementSibling !== sizeChart
        ) {
          sizeChartOrigin.parentElement.insertBefore(
            sizeChart,
            sizeChartOrigin.nextSibling
          );
        }
        if (
          packContents &&
          packContentsOrigin?.parentElement &&
          packContentsOrigin.nextElementSibling !== packContents
        ) {
          packContentsOrigin.parentElement.insertBefore(
            packContents,
            packContentsOrigin.nextSibling
          );
        }
        if (
          nutritionPanel &&
          nutritionOrigin &&
          nutritionOrigin.parentElement &&
          nutritionOrigin.nextElementSibling !== nutritionPanel
        ) {
          nutritionOrigin.parentElement.insertBefore(nutritionPanel, nutritionOrigin.nextSibling);
        }
      };

      const schedulePlacement = () => {
        if (animationFrame !== null) return;
        animationFrame = window.requestAnimationFrame(placeExtras);
      };

      const observer = new MutationObserver(schedulePlacement);
      observer.observe(root, { childList: true, subtree: true });

      if (mobileQuery.addEventListener) {
        mobileQuery.addEventListener("change", schedulePlacement);
      } else {
        mobileQuery.addListener(schedulePlacement);
      }

      schedulePlacement();
    },

    initializeOptions(swatchOptionNames) {
      if (!swatchOptionNames) swatchOptionNames = [];
      this.swatchOptionIndices = this.product.options
        .map((name, index) => (swatchOptionNames.includes(name) ? index : -1))
        .filter((index) => index !== -1);

      this.product.options.forEach((optionName, optionIndex) => {
        this.optionStatus[optionIndex] = {};
        if (this.swatchOptionIndices.includes(optionIndex)) {
          this.optionImages[optionIndex] = {};
        }
        const valuesForOption = [
          ...new Set(
            this.product.variants.map((variant) => variant.options[optionIndex])
          ),
        ];

        valuesForOption.forEach((value) => {
          if (value) {
            this.optionStatus[optionIndex][value] = {
              hidden: false,
              disabled: false,
            };
            if (this.swatchOptionIndices.includes(optionIndex)) {
              const imageVariant = this.product.variants.find(
                (v) => v.options[optionIndex] === value && v.featured_image
              );
              this.optionImages[optionIndex][value] = imageVariant
                ? imageVariant.featured_image.src
                : "";
            }
          }
        });
      });
    },

    optionChanged(optionIndex, value) {
      this.selectedOptions[optionIndex] = value;

      this.updateOptionStatus();

      for (let i = optionIndex + 1; i < this.selectedOptions.length; i++) {
        const currentValue = this.selectedOptions[i];
        
        if (currentValue && this.optionStatus[i][currentValue]?.hidden) {
          this.selectedOptions[i] = null;
        }
      }

      this.updateState();
    },

    selectVariantById(variantId) {
      const variant = this.product.variants.find(
        (item) => item.id === Number(variantId)
      );
      if (!variant) return;

      this.selectedOptions = [...variant.options];
      this.updateState();
    },

    syncVariantSelector(variantId) {
      const variantSelector = this.$root.querySelector("[data-xp-variant-id]");
      if (!variantSelector) return;

      variantSelector.value = String(variantId);
      variantSelector.dispatchEvent(new Event("change", { bubbles: true }));
    },

    openBackInStock(event) {
      if (!this.selectedVariant || this.selectedVariant.available) return;

      this.backInStockVariant = {
        id: this.selectedVariant.id,
        title: this.selectedVariant.title,
        options: [...(this.selectedVariant.options || [])],
      };
      this.backInStockPreviousFocus = event?.currentTarget || null;
      this.backInStockStatus = "idle";
      this.backInStockMessage = "";
      this.backInStockOpen = true;
      this.backInStockBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      this.$nextTick(() => this.$refs.backInStockEmail?.focus());
    },

    closeBackInStock() {
      if (!this.backInStockOpen) return;

      this.backInStockOpen = false;
      document.body.style.overflow = this.backInStockBodyOverflow;

      this.$nextTick(() => this.backInStockPreviousFocus?.focus());
    },

    trapBackInStockFocus(event) {
      const dialog = this.$refs.backInStockDialog;
      if (!dialog) return;

      const focusable = Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    async submitBackInStock() {
      if (
        this.backInStockStatus === "submitting" ||
        !this.backInStockVariant
      ) {
        return;
      }

      const email = this.backInStockEmail.trim();
      const payload = {
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: { email },
              },
            },
            channels: ["EMAIL"],
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${this.backInStockVariant.id}`,
              },
            },
          },
        },
      };

      this.backInStockStatus = "submitting";
      this.backInStockMessage = "";

      try {
        const response = await fetch(
          "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=Xd2hYn",
          {
            method: "POST",
            headers: {
              revision: "2024-02-15",
              "Content-Type": "application/json",
              "X-Klaviyo-Onsite": "1",
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          throw new Error("Back-in-stock subscription failed");
        }

        this.backInStockStatus = "success";
        this.backInStockMessage =
          "You're on the list. We'll email you as soon as it's back.";
        this.$nextTick(() => this.$refs.backInStockSuccess?.focus());
      } catch (error) {
        this.backInStockStatus = "error";
        this.backInStockMessage =
          "We couldn't add your alert. Please check your email address and try again.";
      }
    },

    updateState() {
      let stateChanged;
      do {
        this.updateOptionStatus();
        this.updateSwatchImages(); // Update images based on new status
        stateChanged = this.autoSelectNextAvailable();
      } while (stateChanged);

      this.updateSelectedVariant();
    },

    updateSwatchImages() {
      if (this.swatchOptionIndices.length === 0) return;

      this.swatchOptionIndices.forEach((swatchIndex) => {
        const swatchValues = Object.keys(this.optionStatus[swatchIndex] || {});
        swatchValues.forEach((value) => {
          const potentialSelection = [...this.selectedOptions];
          potentialSelection[swatchIndex] = value;

          const bestVariant =
            this.product.variants.find((variant) => {
              if (!variant.featured_image) return false;
              let matches = true;
              for (let i = 0; i < potentialSelection.length; i++) {
                if (
                  potentialSelection[i] !== null &&
                  potentialSelection[i] !== variant.options[i]
                ) {
                  matches = false;
                  break;
                }
              }
              return matches;
            }) ||
            this.product.variants.find((variant) => {
              if (
                !variant.featured_image ||
                variant.options[swatchIndex] !== value
              )
                return false;
              let matches = true;
              for (let i = 0; i < this.selectedOptions.length; i++) {
                if (i === swatchIndex) continue;
                if (
                  this.selectedOptions[i] !== null &&
                  this.selectedOptions[i] !== variant.options[i]
                ) {
                  matches = false;
                  break;
                }
              }
              return matches;
            }) ||
            this.product.variants.find(
              (v) => v.options[swatchIndex] === value && v.featured_image
            );

          if (bestVariant) {
            this.optionImages[swatchIndex][value] =
              bestVariant.featured_image.src;
          }
        });
      });
    },

    getSwatchImage(optionIndex, value) {
      const imageUrl = this.optionImages[optionIndex]
        ? this.optionImages[optionIndex][value]
        : null;
      if (!imageUrl)
        return "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="; // Transparent pixel
      // Shopify's `replace` trick for getting a different image size from the URL
      return imageUrl.replace(
        /(\.jpg|\.png|\.jpeg|\.gif)\?/,
        "_100x100_crop_center$1?"
      );
    },

    updateOptionStatus() {
      this.product.options.forEach((optionName, optionIndex) => {
        const valuesForOption = [
          ...new Set(
            this.product.variants.map((variant) => variant.options[optionIndex])
          ),
        ];
        valuesForOption.forEach((value) => {
          if (!value) return;

          // Find variants that match the current value AND all PRECEDING option selections.
          const potentialMatches = this.product.variants.filter((variant) => {
            for (let i = 0; i < optionIndex; i++) {
              if (
                this.selectedOptions[i] !== null &&
                variant.options[i] !== this.selectedOptions[i]
              ) {
                return false;
              }
            }
            return variant.options[optionIndex] === value;
          });

          // If no such variant exists, this option is impossible.
          if (potentialMatches.length === 0) {
            this.optionStatus[optionIndex][value].hidden = true;
            this.optionStatus[optionIndex][value].disabled = true;
          } else {
            // Otherwise, it's possible. Check if any of the potential variants are available.
            this.optionStatus[optionIndex][value].hidden = false;
            const isAvailable = potentialMatches.some(
              (variant) => variant.available
            );
            this.optionStatus[optionIndex][value].disabled = !isAvailable;
          }
        });
      });
    },

    autoSelectNextAvailable() {
      let changed = false;
      for (let i = 0; i < this.selectedOptions.length; i++) {
        if (
          this.selectedOptions[i] === null ||
          (this.optionStatus[i] &&
            this.optionStatus[i][this.selectedOptions[i]] &&
            this.optionStatus[i][this.selectedOptions[i]].hidden)
        ) {
          const valuesForOption = [
            ...new Set(
              this.product.variants.map((variant) => variant.options[i])
            ),
          ];
          const firstAvailable = valuesForOption.find((value) => {
            return (
              value &&
              this.optionStatus[i][value] &&
              !this.optionStatus[i][value].hidden
            );
          });
          const newValue = firstAvailable || null;
          if (this.selectedOptions[i] !== newValue) {
            this.selectedOptions[i] = newValue;
            changed = true;
          }
        }
      }
      return changed;
    },

    updateSelectedVariant() {
      if (this.selectedOptions.every((opt) => opt !== null)) {
        const foundVariant = this.product.variants.find((variant) => {
          return variant.options.every(
            (opt, index) => opt === this.selectedOptions[index]
          );
        });
        this.selectedVariant = foundVariant
          ? this.applyAcademiaSaleDiscount(foundVariant)
          : null;
      } else {
        this.selectedVariant = null;
      }

      window.dispatchEvent(new CustomEvent("variant:updated"));
    },

    /**
     * Applies the Academia / Sale Discount pricing rule to a variant,
     * returning a copy with `price`/`compare_at_price` overridden. Mirrors
     * extensions/academia-sale-discount/src/run.js in the xmiles-functions
     * app so displayed prices match what the Function actually charges.
     *
     * Never mutates the source variant — this.product.variants must stay
     * at raw/original prices since it's re-read on every variant switch.
     */
    applyAcademiaSaleDiscount(variant) {
      if (!variant) return variant;

      const override = this.priceOverride;
      const variantOverride = (override && override[variant.id]) || {};
      const academiaDiscount = Number(variantOverride.academia_discount) || 0;
      const saleDiscount = Number(variantOverride.sale_discount) || 0;

      let percentage = 0;
      let discountType = null;

      if (this.outlet) {
        if (this.hasAcademiaAccess) {
          if (academiaDiscount >= saleDiscount) {
            percentage = academiaDiscount;
            discountType = "academia";
          } else {
            percentage = saleDiscount;
            discountType = "sale";
          }
        } else {
          percentage = saleDiscount;
          discountType = "sale";
        }
      } else if (this.hasAcademiaAccess) {
        percentage = academiaDiscount;
        discountType = "academia";
      }

      if (percentage <= 0) {
        return {
          ...variant,
          academiaSaleDiscountActive: false,
          academiaSaleDiscountType: null,
        };
      }

      // Shopify rounds the percentage discount down to a whole penny per unit,
      // then subtracts it from the original price. This keeps PDP pricing in
      // step with cart/checkout for fractional-penny discounts.
      const discountAmount = Math.floor((variant.price * percentage) / 100);
      const discountedPrice = variant.price - discountAmount;

      return {
        ...variant,
        price: discountedPrice,
        // Keep the compare-at price for the crossed-out price and savings.
        // Fall back to the regular price when no compare-at price is set.
        compare_at_price: variant.compare_at_price > 0 ? variant.compare_at_price : variant.price,
        academiaSaleDiscountActive: true,
        academiaSaleDiscountType: discountType,
      };
    },

    // Your original methods
    submitForm(form) {
      const formData = new FormData(form);
      fetch("/cart/add.js", {
        method: "POST",
        body: formData,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
        .then((response) => response.json())
        .then((data) => {
          document.dispatchEvent(new CustomEvent("cart:drawer:open"));
        })
        .catch((error) => console.error("Error:", error));
    },

    updateMainImage() {
      if (this.selectedVariant && this.selectedVariant.featured_image) {
        const mainImageContainer = document.querySelector(".xp-gallery");
        if (!mainImageContainer) return;
        const newImg = document.createElement("img");
        newImg.src = this.selectedVariant.featured_image.src;
        newImg.alt = this.selectedVariant.featured_image.alt;
        newImg.className = "xp-gallery__image";
        const currentImage = mainImageContainer.querySelector(".xp-gallery__image");
        if (currentImage) {
          currentImage.replaceWith(newImg);
        } else {
          mainImageContainer.prepend(newImg);
        }
      }
    },

    updateUrlWithVariantId() {
      if (this.selectedVariant && this.selectedVariant.id) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", this.selectedVariant.id);
        window.history.replaceState({}, "", url.toString());
      }
    },

    selectedVariantNutrition() {
      if (!this.selectedVariant) return {};
      return this.nutritionInfo[this.selectedVariant.id] || {};
    },

    selectedVariantBbe() {
      return String(this.selectedVariantNutrition().bbe || "").trim();
    },

    usesXplusMixMatchPrice() {
      return Boolean(this.xplus && this.selectedVariantNutrition().mix_match);
    },

    offerSavingAmount() {
      if (!this.selectedVariant) return 0;

      if (this.usesXplusMixMatchPrice()) {
        return Math.max(0, Math.round(this.selectedVariant.price * 0.08));
      }

      const compareAtPrice = Number(this.selectedVariant.compare_at_price) || 0;
      const price = Number(this.selectedVariant.price) || 0;
      return compareAtPrice > price ? compareAtPrice - price : 0;
    },

    offerSavingPercentage() {
      if (!this.selectedVariant) return 0;
      if (this.usesXplusMixMatchPrice()) return 8;

      const compareAtPrice = Number(this.selectedVariant.compare_at_price) || 0;
      if (compareAtPrice <= 0) return 0;
      return Math.round((this.offerSavingAmount() / compareAtPrice) * 100);
    },

    offerSavingText() {
      const amount = this.offerSavingAmount();
      if (amount <= 0) return "";
      return `Save ${this.formatMoney(amount)} (${this.offerSavingPercentage()}%)`;
    },

    offerLabel() {
      if (!this.selectedVariant) return "";

      const productLabel = String(this.productLabel || "").trim();
      const hideGenericPdpLabel = ["save", "sale", "mix & match"].includes(
        productLabel.toLowerCase()
      );
      const isMultiPackDeals = productLabel
        .toLowerCase()
        .includes("multi-pack deals");

      if (this.selectedVariant.academiaSaleDiscountType === "academia") {
        return "Academia discount";
      }
      if (
        isMultiPackDeals &&
        Number(this.priceOverride?.[this.selectedVariant.id]?.sale_discount) > 0
      ) {
        return "";
      }
      if (this.selectedVariant.academiaSaleDiscountType === "sale") {
        return hideGenericPdpLabel ? "" : productLabel;
      }
      if (productLabel) {
        if (hideGenericPdpLabel) return "";
        if (isMultiPackDeals && this.offerSavingAmount() <= 0) return "";
        return productLabel;
      }
      return "";
    },

    formatMoney(totalCents, showSubUnit = false) {
      if (typeof totalCents !== "number" || isNaN(totalCents)) return "";

      // 1. Identify the currency symbol from your Shopify money format
      // Example: "£{{amount}}" -> symbol is "£"
      const moneyFormat = this.moneyFormat || "£{{amount}}";
      const majorSymbol = moneyFormat.replace(/\{\{\s*amount.*\s*\}\}/, "").trim();

      // 2. Map of sub-unit symbols based on the major symbol
      const subunitMap = {
        "£": "p",
        "$": "¢",
        "€": "c",
        "€ ": "c"
      };
      const subunitSymbol = subunitMap[majorSymbol] || "";

      // 3. Logic: If it's less than 1 major unit (e.g., less than £1.00)
      if (totalCents < 100 && subunitSymbol && showSubUnit) {
        return (totalCents.toFixed(1) + subunitSymbol).replace(".0", "");
      }

      // 4. Default: Standard Shopify formatting for amounts >= 1.00
      let amount = (totalCents / 100).toFixed(2);
      
      // Use regex to put the amount into the correct slot (handles prefix/suffix currencies)
      return moneyFormat.replace(/\{\{\s*amount.*\s*\}\}/, amount);
    },

    getDeliveryDate(daysToAdd = 5) {
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + daysToAdd);
      const startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 2);
      const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return "th";
        switch (day % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      if (startDate.getMonth() !== endDate.getMonth()) {
        return `${startDate.getDate()}${getOrdinalSuffix(
          startDate.getDate()
        )} ${
          months[startDate.getMonth()]
        } - ${endDate.getDate()}${getOrdinalSuffix(endDate.getDate())} ${
          months[endDate.getMonth()]
        }`;
      } else {
        return `${startDate.getDate()}${getOrdinalSuffix(
          startDate.getDate()
        )} - ${endDate.getDate()}${getOrdinalSuffix(endDate.getDate())} ${
          months[endDate.getMonth()]
        }`;
      }
    },
  }));
});
